import React, { useCallback, useRef, useState } from 'react';
import classes from './Modal.module.scss';
import { WithChildren } from '../../../models/children-props';
import { SquareButton } from '../../actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import classNames from 'classnames';
import { ButtonViewProps } from '../../actions/Button/Button.view';
import { CSSTransition } from 'react-transition-group';
import { useDidMount } from 'hooks/useDidMount';
import { Portal } from '../Portal';
import { useDimensions } from '../../../hooks/useDimensions';
import { ScrollProvider } from '../../../providers/ScrollProvider';
import { Void } from '../../../models/basic-objects';
import { OutsideClickWrapper } from '../../actions/OutsideClickWrapper';
import { AllowedChildrenType, validateChildren } from '../../../helpers/validateChildren';

export type FooterContentTemplate = (args: ModalViewProps) => JSX.Element;

export type PrimaryButtonOptional = {
  primaryButtonTitle?: string;
  primaryButtonAction?: never;
  primaryButtonDisabled?: never;
  primaryButtonLoading?: never;
  primaryButtonColor?: never;
  primaryButtonType?: never;
  footerContent?: never;
};

export type PrimaryButtonMandatory = {
  primaryButtonTitle: string;
  primaryButtonAction: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonLoading?: boolean;
  primaryButtonColor?: ButtonColorsEnum;
  primaryButtonType?: Omit<ButtonViewProps['type'], 'a'>;
  footerContent?: never;
};

export type SecondaryButtonOptional = {
  secondaryButtonTitle?: string;
  secondaryButtonAction?: never;
  secondaryButtonDisabled?: never;
  secondaryButtonLoading?: never;
  secondaryButtonColor?: never;
  secondaryButtonType?: never;
  footerContent?: never;
};

export type SecondaryButtonMandatory = {
  secondaryButtonTitle: string;
  secondaryButtonAction: () => void;
  secondaryButtonDisabled?: boolean;
  secondaryButtonLoading?: boolean;
  secondaryButtonColor?: ButtonColorsEnum;
  secondaryButtonType?: Omit<ButtonViewProps['type'], 'a'>;
  footerContent?: never;
};

export type PrimaryButtonType = PrimaryButtonOptional | PrimaryButtonMandatory;

export type SecondaryButtonType = SecondaryButtonOptional | SecondaryButtonMandatory;

type FooterContentType = {
  primaryButtonTitle?: never;
  primaryButtonAction?: never;
  primaryButtonDisabled?: never;
  primaryButtonLoading?: never;
  primaryButtonColor?: never;
  primaryButtonType?: never;
  secondaryButtonTitle?: never;
  secondaryButtonAction?: never;
  secondaryButtonDisabled?: never;
  secondaryButtonLoading?: never;
  secondaryButtonColor?: never;
  secondaryButtonType?: never;
  footerContent?: FooterContentTemplate;
};

export type ModalViewProps =
  | (WithChildren & PrimaryButtonType & SecondaryButtonType)
  | ((WithChildren & FooterContentType) & {
      contentClass?: string;
      title?: string | React.ReactNode | JSX.Element;
      onClose: VoidFunction;
      showCloseIcon: boolean;
      size?: 'small' | 'large' | 'fluid' | 'hugging' | 'zoom';
      compensateForScroll?: boolean;
      omitPortal?: true;
      isBorderLess?: boolean;
      scrollToBottomHandler?: Void;
      scrollToTopHandler?: Void;
      clickOnOutsideHandler: VoidFunction;
      isFilter?: boolean;
      showOverlay?: boolean;
    });

export const AllowedChildrenForModalHeader: AllowedChildrenType[] = ['string', 'div', 'a'];

export const footerModalButton = (
  title: string,
  isPrimary: boolean,
  hasPrimaryTitle: boolean,
  hasSecondaryTitle: boolean,
  color: ButtonColorsEnum,
  size: ModalViewProps['size'],
  action?: () => void,
  isDisabled?: boolean,
  isLoading?: boolean,
  type?: Omit<ButtonViewProps['type'], 'a'>
): JSX.Element => {
  const variant = isPrimary ? 'solid' : 'hollow';
  const gridButtonClasses = classNames(
    isPrimary ? 'small-order-1 medium-order-2' : 'small-order-2 medium-order-1',
    'cell',
    'small-expanded',
    {
      'medium-shrink': (hasPrimaryTitle && hasSecondaryTitle) || size !== 'small',
      'margin-bottom-small-16 margin-bottom-medium-0': isPrimary && hasPrimaryTitle && hasSecondaryTitle,
      [classes.PrimaryButton]: isPrimary
    }
  );

  isDisabled = !!isDisabled;

  return (
    <div className={gridButtonClasses}>
      <Button
        type={(type as ButtonViewProps['type']) || 'button'}
        {...(action ? { action: action } : null)}
        variant={variant}
        size={'large'}
        color={color}
        disabled={isDisabled}
        loading={isLoading}
        expanded={true}>
        {title}
      </Button>
    </div>
  );
};
export const ModalView = ({ showOverlay = true, ...props }: ModalViewProps): JSX.Element => {
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const ref = useRef(null);

  useDidMount(() => {
    // we need to setMount to true, since we want to trigger animation (fade) only when the component has been completely mounted.
    setMount(true);
    return () => setMount(false);
  });

  const shouldRenderFooter = props.primaryButtonTitle || props.secondaryButtonTitle || props.footerContent;

  const size = props.size || 'small';
  const contentClasses = classNames(
    classes.Content,
    {
      [classes.ContentNoButtons]: !props.primaryButtonTitle && !props.secondaryButtonAction,
      [classes.ContentTwoButton]: props.primaryButtonTitle && props.secondaryButtonTitle,
      [classes.ContentOneButton]:
        !(props.primaryButtonTitle && props.secondaryButtonTitle) &&
        (props.primaryButtonTitle || props.secondaryButtonTitle),
      'margin-top-small-24': !props.title && !props.footerContent,
      [classes.ContentSmall]: size === 'small' && !props.secondaryButtonTitle,
      [classes.ContentSmallTwoButtons]: size === 'small' && !!props.secondaryButtonTitle,
      [classes.ContentLarge]: size !== 'small' && !props.secondaryButtonTitle,
      [classes.ContentLargeNoFooter]: size !== 'small' && !shouldRenderFooter,
      [classes.ContentLargeTwoButtons]: size !== 'small' && !!props.secondaryButtonTitle,
      [classes.ContentFluid]: size === 'fluid' && !props.primaryButtonTitle && !props.secondaryButtonTitle,
      [classes.ContentFluidTwoButtons]: size === 'fluid' && !!props.secondaryButtonTitle,
      [classes.ContentFluidNoBorder]: props.isBorderLess,
      [classes.ContentHugging]: size === 'hugging' && !props.primaryButtonTitle && !props.secondaryButtonTitle,
      [classes.ContentHuggingWithButtons]:
        size === 'hugging' && (props.primaryButtonTitle || props.secondaryButtonTitle),
      [classes.ContentZoom]: size === 'zoom',
      [classes.ContentIsFilter]: props.isFilter
    },
    props.contentClass
  );
  const showCloseIcon = props.showCloseIcon || typeof props.showCloseIcon == 'undefined';

  const modalWrapperClasses = classNames(classes.Modal, 'grid-container', 'full', {
    [classes.Overlay]: showOverlay
  });

  const modalContainerClasses = classNames(
    classes.ModalContainer,
    {
      [classes.ModalContainerNoButtons]: !props.primaryButtonTitle && !props.secondaryButtonAction,
      [classes.ModalContainerTwoButtons]: props.primaryButtonTitle && props.secondaryButtonTitle,
      [classes.ModalContainerOneButton]:
        !(props.primaryButtonTitle && props.secondaryButtonTitle) &&
        (props.primaryButtonTitle || props.secondaryButtonTitle)
    },
    size === 'small'
      ? classes.ModalContainerSmall
      : size === 'fluid'
      ? classes.ModalContainerFluid
      : size === 'hugging'
      ? classes.ModalContainerHugging
      : size === 'zoom'
      ? classes.ModalContainerZoom
      : classes.ModalContainerLarge
  );

  const primaryButton = props.primaryButtonTitle
    ? footerModalButton(
        props.primaryButtonTitle,
        true,
        !!props.primaryButtonTitle,
        !!props.secondaryButtonTitle,
        props.primaryButtonColor || ButtonColorsEnum.secondary,
        size,
        props.primaryButtonAction,
        props.primaryButtonDisabled,
        props.primaryButtonLoading,
        props.primaryButtonType
      )
    : null;
  const secondaryButton = props.secondaryButtonTitle
    ? footerModalButton(
        props.secondaryButtonTitle,
        false,
        !!props.primaryButtonTitle,
        !!props.secondaryButtonTitle,
        props.secondaryButtonColor || ButtonColorsEnum.secondary,
        size,
        props.secondaryButtonAction,
        props.secondaryButtonDisabled,
        props.secondaryButtonLoading,
        props.secondaryButtonType
      )
    : null;

  const mainContentRef = useRef<HTMLDivElement>(null);
  const parentRef = mainContentRef.current?.parentElement || null;
  const { hasScrollbar } = useDimensions({ current: parentRef });
  const HasDualButtons = props.primaryButtonTitle && props.secondaryButtonTitle;

  const buttonContainerClasses = classNames(
    classes.ButtonContainer,
    size === 'small'
      ? classes.ButtonContainerSmall
      : size === 'hugging'
      ? classes.ButtonContainerHugging
      : classes.ButtonContainerLarge,
    'grid-x',
    props.primaryButtonTitle && props.secondaryButtonTitle ? 'align-justify' : 'align-right',
    {
      [classes.InScrollMode]: hasScrollbar,
      [classes.HasDualButtons]: HasDualButtons
    }
  );

  const [footerHeight, setFooterHeight] = useState<number | null>(null);
  const footerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setFooterHeight(node.clientHeight);
    }
  }, []);

  const modalFooter = shouldRenderFooter ? (
    <div ref={footerRefCallback}>
      {(props.footerContent && props.footerContent(props)) || (
        <div className={buttonContainerClasses}>
          {secondaryButton}
          {primaryButton}
        </div>
      )}
    </div>
  ) : null;

  if (props.footerContent && footerHeight && mainContentRef.current && cssRef.current) {
    mainContentRef.current.style.maxHeight = `calc(95vh - 64px - ${footerHeight}px)`; // Height - Header - Footer
  }

  if (process.env.NODE_ENV !== 'production') {
    validateChildren(props.title, AllowedChildrenForModalHeader);
  }

  const modalHeader =
    props.title || showCloseIcon ? (
      <div className={`${classes.Header} grid-x align-justify`}>
        {props.title && typeof props.title === 'string' ? (
          <h3 className={`${classes.Title}`}>{props.title}</h3>
        ) : (
          props.title
        )}
        {showCloseIcon && (
          <div className={classes.CloseButtonContainer}>
            <SquareButton type={'transparent'} icon={SquareButtonIconsEnum.close} action={props.onClose} />
          </div>
        )}
      </div>
    ) : null;

  const content = (
    <CSSTransition in={mount} timeout={300} classNames={'fade'} unmountOnExit nodeRef={cssRef}>
      <div className={modalWrapperClasses} ref={cssRef}>
        <div className={`grid-y align-center full-height`}>
          <div className={`grid-x align-center`}>
            <OutsideClickWrapper
              onClickOutside={props.clickOnOutsideHandler}
              onFocusOutside={props.clickOnOutsideHandler}
              ref={ref}
              className={modalContainerClasses}>
              {modalHeader}
              <ScrollProvider
                className={contentClasses}
                scrollToBottomHandler={props.scrollToBottomHandler}
                scrollToTopHandler={props.scrollToTopHandler}>
                <div ref={mainContentRef}>{props.children}</div>
              </ScrollProvider>
              {modalFooter}
            </OutsideClickWrapper>
          </div>
        </div>
      </div>
    </CSSTransition>
  );

  return props.omitPortal === true ? content : <Portal>{content}</Portal>;
};
